/**
 * /diagnostic/history — F-131 Diagnostic History.
 *
 * The third diagnostic view (Results · Roadmap · History): every past diagnostic run, newest
 * first — date, the frozen 0–1000, movement since the previous run, lifecycle stage, and where
 * the constraint sat. A row opens inline to show what crossed into place in that period. A
 * paid surface behind the roadmap flag, like the Roadmap tab.
 */
import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useOrganization } from '@/contexts';
import { isRoadmapEnabled, loadFeatureFlags } from '@/lib/feature-flags';
import { useRoadmapAccess } from '@/hooks/use-roadmap';
import { useRoadmapHistory } from '@/hooks/use-roadmap-history';
import { DiagnosticViewTabs } from '@/components/roadmap/DiagnosticViewTabs';
import { RoadmapAccessGate } from '@/components/roadmap/RoadmapAccessGate';
import { HistoryRow } from '@/components/roadmap/HistoryRow';

export const Route = createFileRoute('/diagnostic/history')({
  beforeLoad: async ({ context }) => {
    await loadFeatureFlags(context.queryClient);
    if (!isRoadmapEnabled()) throw redirect({ to: '/diagnostic/results' });
  },
  component: GatedHistoryPage,
});

function GatedHistoryPage() {
  return (
    <RoadmapAccessGate>
      <HistoryPage />
    </RoadmapAccessGate>
  );
}

export function HistoryPage() {
  const { organization } = useOrganization();
  const orgId = organization?.id ?? null;
  const { data: access } = useRoadmapAccess(orgId);
  const { rows, isLoading, isError, error, snapshotsUnavailable } = useRoadmapHistory(orgId, access?.canRead ?? false);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="container mx-auto max-w-5xl space-y-6 px-4 py-6">
      <DiagnosticViewTabs />

      <div>
        <h1 className="text-3xl font-bold leading-tight text-foreground">Your Diagnostic History</h1>
        <p className="text-sm text-muted-foreground">
          Every diagnostic you've completed — open one to see that snapshot in time.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading your history…
        </div>
      )}

      {isError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4" />
          {error instanceof Error ? error.message : 'Could not load your history.'}
        </div>
      )}

      {snapshotsUnavailable && rows && rows.length > 0 && (
        <p className="text-xs text-muted-foreground">Frozen scores could not be loaded; the runs are listed without them.</p>
      )}

      {rows && rows.length <= 1 && !isLoading && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          Past diagnostics will appear here after your next retake.
        </p>
      )}

      {rows && rows.length > 0 && (
        <ol className="space-y-3">
          {rows.map((run) => (
            <HistoryRow
              key={run.sessionId}
              run={run}
              expanded={expanded === run.sessionId}
              onToggle={() => setExpanded(expanded === run.sessionId ? null : run.sessionId)}
            />
          ))}
        </ol>
      )}
    </div>
  );
}
